import React, { useEffect, useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { toggleMapFilterToggle } from '@actions/map-actions';
import {
  setLayerVisible,
  fetchLayerConfig
} from '@actions/layers-actions';
import { parentOrderedLayersSelector } from '@selectors/layers-selector';
import FilterCheckbox from '@shared/filter-checkbox';
import { filterLayerIcon } from '@utils/icon-utils';
import BaseTrayFilter from './base-filter';
import './filter.scss';

const LayersFilter = ({ config }) => {
  const dispatch = useDispatch();
  const filterEnabled = useSelector(state => state.map.filterToggles.layer);
  const layers = useSelector(parentOrderedLayersSelector);

  useEffect(() => {
    if (layers.length === 0) {
      dispatch(fetchLayerConfig());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onToggleClick = useCallback(() => {
    dispatch(toggleMapFilterToggle('layer'));
  }, [dispatch]);

  const getChildren = useCallback(
    layer => layers.filter(item => item.parent_id === layer.id),
    [layers]
  );

  const getChecked = useCallback(layer => {
    if (layer.children) {
      const children = getChildren(layer);
      const allVisible = children.every(item => item.visible === true);
      return allVisible;
    }
    return layer.visible;
  }, [getChildren]);

  const toggleLayer = useCallback(layer => {
    if (layer.children) {
      const isAllChecked = getChecked(layer);
      const children = getChildren(layer);
      children.forEach(item => {
        dispatch(setLayerVisible(item.name, !isAllChecked));
      });
      return;
    }
    dispatch(setLayerVisible(layer.name, (!layer || !layer.visible)));
  }, [dispatch, getChecked, getChildren]);

  const renderLayer = useCallback(layer => (
    <div styleName="list-item" key={layer.name}>
      {layer.parent_id && <div styleName="children-indent" />}
      <div styleName="checkbox">
        <FilterCheckbox
          checked={getChecked(layer)}
          disabled={!filterEnabled}
          onChange={() => toggleLayer(layer)}
          name={layer.label || layer.name}
        />
      </div>
      {!layer.children && (
        <div styleName="icon">
          {filterLayerIcon(layer.icon, layer.label)}
        </div>
      )}
      <div styleName="label">
        {layer.label}
      </div>
    </div>
  ), [filterEnabled, getChecked, toggleLayer]);

  const renderContent = useCallback(() => (
    <div styleName="content-list-container">
      {layers.map(layer => renderLayer(layer))}
    </div>
  ), [layers, renderLayer]);

  return (
    <BaseTrayFilter
      config={config}
      filterEnabled={filterEnabled}
      onToggleClick={onToggleClick}
      renderContent={renderContent}
    />
  );
};

LayersFilter.propTypes = {
  config: PropTypes.object
};

export default memo(LayersFilter);
